(() => {
  'use strict';

  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  function initHeader(root = document) {
    const header = root.querySelector('[data-moab-editorial-header]') || document.querySelector('[data-moab-editorial-header]');
    if (!header || header.dataset.initialized === 'true') return;
    header.dataset.initialized = 'true';

    const isHome = header.dataset.home === 'true';
    const transparentHome = header.dataset.transparentHome === 'true';
    const openBtn = document.querySelector('[data-moab-menu-open]');
    const closeBtn = document.querySelector('[data-moab-menu-close]');
    const drawer = document.querySelector('[data-moab-menu-drawer]');

    const updateHeader = () => {
      const shouldBeTransparent = isHome && transparentHome && window.scrollY < 12;
      header.classList.toggle('is-transparent', shouldBeTransparent);
    };

    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });

    const setDrawer = (open) => {
      if (!drawer) return;
      drawer.classList.toggle('is-open', open);
      drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
      if (openBtn) openBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.classList.toggle('moab-drawer-open', open);
    };

    openBtn?.addEventListener('click', () => setDrawer(true));
    closeBtn?.addEventListener('click', () => setDrawer(false));
    drawer?.addEventListener('click', (event) => {
      if (event.target.closest('a')) setDrawer(false);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') setDrawer(false);
    });
  }

  function initStory(wrapper) {
    if (!wrapper || wrapper.dataset.initialized === 'true') return;
    wrapper.dataset.initialized = 'true';

    const slides = qsa('[data-moab-story-slide]', wrapper);
    if (!slides.length) return;

    const dotsWrap = wrapper.querySelector('[data-moab-story-dots]');
    const dots = dotsWrap ? qsa('.moab-story-dot', dotsWrap) : [];
    const back = wrapper.querySelector('[data-moab-story-back]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const controller = new AbortController();
    const signal = controller.signal;
    wrapper._moabCleanup = () => controller.abort();

    let slideHeight = 0;
    let lastWidth = window.innerWidth;
    let ticking = false;

    // Sticky stops working when a theme ancestor clips overflow.
    let parent = wrapper.parentElement;
    while (parent && parent !== document.body) {
      const style = window.getComputedStyle(parent);
      const overflow = `${style.overflow} ${style.overflowY}`;
      if (overflow.includes('hidden') || overflow.includes('scroll') || overflow.includes('auto')) {
        parent.style.setProperty('overflow', 'visible', 'important');
        parent.style.setProperty('overflow-y', 'visible', 'important');
      }
      if (style.maxHeight && style.maxHeight !== 'none') parent.style.setProperty('max-height', 'none', 'important');
      parent = parent.parentElement;
    }

    const wrapperTop = () => wrapper.getBoundingClientRect().top + window.scrollY;
    const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
    const pushAmount = () => Number(isMobile() ? wrapper.dataset.mobilePush : wrapper.dataset.desktopPush) || 0;
    const parallaxAmount = () => Number(isMobile() ? wrapper.dataset.mobileParallax : wrapper.dataset.desktopParallax) || 0;

    const measure = () => {
      slideHeight = Math.max(1, slides[0].getBoundingClientRect().height);
      wrapper.style.height = `${Math.ceil(slides.length * slideHeight)}px`;
    };

    const scrollToSlide = (index) => {
      if (!slideHeight) measure();
      const target = wrapperTop() + clamp(index, 0, slides.length - 1) * slideHeight;
      window.scrollTo({ top: target, behavior: reduceMotion ? 'auto' : 'smooth' });
    };

    dots.forEach((dot) => {
      dot.addEventListener('click', () => scrollToSlide(Number(dot.dataset.index || 0)), { signal });
    });
    back?.addEventListener('click', () => scrollToSlide(0), { signal });

    const paint = () => {
      ticking = false;
      if (!slideHeight) measure();

      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const top = wrapperTop();
      const bottom = top + slides.length * slideHeight;
      const inside = scrollTop >= top && scrollTop < bottom - slideHeight + 1;
      const scrolledIn = scrollTop - top;
      let current = Math.floor(scrolledIn / slideHeight + 0.35);
      current = clamp(current, 0, slides.length - 1);

      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === current));
      dotsWrap?.classList.toggle('is-visible', inside);
      back?.classList.toggle('is-visible', inside && current > 0);

      const push = pushAmount();
      const parallax = parallaxAmount();

      slides.forEach((slide, i) => {
        if (reduceMotion) {
          slide.style.transform = 'translate3d(0,0,0)';
          qsa('.moab-story-media', slide).forEach((media) => { media.style.transform = 'translate3d(0,0,0)'; });
          return;
        }

        const slideStart = top + i * slideHeight;
        const progress = clamp((scrollTop - slideStart) / slideHeight, 0, 1);
        const isLast = i === slides.length - 1;
        const y = isLast ? 0 : -progress * push * slideHeight;
        slide.style.transform = `translate3d(0,${y.toFixed(2)}px,0)`;
        qsa('.moab-story-media', slide).forEach((media) => {
          const mediaY = isLast ? 0 : -progress * parallax;
          media.style.transform = `translate3d(0,${mediaY.toFixed(3)}%,0)`;
        });
      });
    };

    const requestPaint = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(paint);
    };

    measure();
    paint();
    window.addEventListener('scroll', requestPaint, { passive: true, signal });
    window.addEventListener('orientationchange', () => setTimeout(() => { measure(); paint(); }, 140), { signal });
    window.addEventListener('resize', () => {
      const width = window.innerWidth;
      // Ignore mobile browser chrome height-only changes; remeasure on actual layout width change.
      if (Math.abs(width - lastWidth) > 2) {
        lastWidth = width;
        measure();
      }
      requestPaint();
    }, { passive: true, signal });
    window.addEventListener('load', () => { measure(); paint(); }, { once: true, signal });
  }

  function initFeatured(section) {
    if (!section || section.dataset.initialized === 'true') return;
    section.dataset.initialized = 'true';
    const track = section.querySelector('[data-moab-featured-track]');
    const prev = section.querySelector('[data-moab-featured-prev]');
    const next = section.querySelector('[data-moab-featured-next]');
    if (!track) return;
    const move = (direction) => track.scrollBy({ left: direction * track.clientWidth * 0.82, behavior: 'smooth' });
    prev?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));
  }

  function initAll(root = document) {
    initHeader(root);
    qsa('[data-moab-story]', root).forEach(initStory);
    qsa('[data-moab-featured]', root).forEach(initFeatured);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const wrapper = event.target.querySelector?.('[data-moab-story]');
    wrapper?._moabCleanup?.();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target.closest?.('[data-moab-story-slide]');
    const wrapper = block?.closest?.('[data-moab-story]');
    if (!block || !wrapper) return;
    const slides = qsa('[data-moab-story-slide]', wrapper);
    const first = slides[0];
    if (!first) return;
    const height = Math.max(1, first.getBoundingClientRect().height);
    const index = Number(block.dataset.slideIndex || 0);
    window.scrollTo({ top: wrapper.getBoundingClientRect().top + window.scrollY + index * height, behavior: 'smooth' });
  });
})();
